import API from './api';
import * as paymentCardValidator from 'card-validator';
import { parsePhoneNumber } from 'awesome-phonenumber';
import { defineRule } from 'vee-validate';
import { email, required, alpha_spaces, confirmed } from '@vee-validate/rules';
import { convertFromBraintreeCardType, PAYMENT_CARD_MAPPING } from './constants';

defineRule('alphaSpaces', (value) => {
    if (!alpha_spaces(value)) {
        return 'This field may only contain alphabetic characters or spaces.';
    }
    return true;
});

defineRule('email', (value) => {
    if (!email(value)) {
        return 'This field should be an email.';
    }
    return true;
});

defineRule('confirmed', (value, [target], ctx) => {
    if (target === undefined) {
        return true;
    }
    if (value === target) {
        return true;
    }
    return 'Passwords must be identical.';
});

defineRule('confirmedEmail', (value, [target], ctx) => {
    if (target === undefined) {
        return true;
    }
    if (value === target) {
        return true;
    }
    return 'Emails must be identical.';
});

defineRule('required', (value, [params]) => {
    if (params === 'allowNull' && value === null) {
        return true;
    }
    if (params === 'allowFalse' && value === false) {
        return true;
    }
    if (!required(value)) {
        return 'This field is required.';
    }
    return true;
});

defineRule('minusOneThrowsRequired', (value) => {
    if (value == -1) {
        return 'This field is required.';
    }
    return true;
});

defineRule('validAccountPassword', (value) => {
    if (!value) {
        return true;
    }
    const validPassRegx = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])[\x21-\x7E]{8,}$/;
    if (validPassRegx.test(value)) {
        return true;
    }
    return 'Password should be more than 8 characters and include at least 1 uppercase letter, 1 lowercase letter and 1 digit.';
});

defineRule('validInteger', (value) => {
    const isNormalInteger = (str) => {
        str = str.trim();
        if (!str || /^0{2}/.test(str)) {
            return false;
        }
        let n = Math.floor(Number(str));
        return n !== Infinity && String(n) === str && n >= 0;
    };

    if (!value || isNormalInteger(`${value}`)) {
        return true;
    }
    return 'Must be valid integer value.';
});

defineRule('validPaymentCardNumber', (value, params) => {
    const { validPaymentCardTypes = [] } = params;
    const { isValid, card } = paymentCardValidator.number(value);
    if (card && validPaymentCardTypes.length > 0) {
        const cardType = convertFromBraintreeCardType(card.type);
        if (!validPaymentCardTypes.find((ct) => ct.id === cardType)) {
            const cardTypeText = PAYMENT_CARD_MAPPING[cardType] || 'This';
            const cardTypesText = validPaymentCardTypes.map((ct) => ct.name).join(', ');
            return `${cardTypeText} card is not supported. We only support ${cardTypesText}.`;
        }
    }

    if (isValid) {
        return true;
    }

    return 'Card number is invalid.';
});

defineRule('validPaymentCardholderName', (value) => {
    const { isValid, isPotentiallyValid } = paymentCardValidator.cardholderName(value);
    if (isPotentiallyValid || isValid) {
        return true;
    }
    return 'Card holder is invalid. Please, re-check.';
});

defineRule('validPaymentCardExpiry', (value) => {
    const { isValid } = paymentCardValidator.expirationDate(value);
    if (isValid) {
        return true;
    }
    return 'Card expiration is invalid. Please, re-check.';
});

defineRule('validPaymentCardCvv', (value, [maxLength]) => {
    const { isValid } = paymentCardValidator.cvv(value, maxLength);
    if (isValid) {
        return true;
    }
    return 'Card code is invalid. Please, re-check.';
});

defineRule('validPaymentCardCVC', (value) => {
    if (value.length === 3 || value.length === 4) {
        return true;
    }
    return 'Card code is invalid. Please, re-check.';
});

defineRule('validPhoneNumber', (value) => {
    if (!value || parsePhoneNumber(value).isValid()) {
        return true;
    }
    return 'Invalid phone number.';
});

defineRule('validPositiveInteger', (value) => {
    const isPositiveInteger = (str) => {
        return /^[1-9]\d*$/.test(str);
    };

    if (!value || isPositiveInteger(`${value}`)) {
        return true;
    }
    return 'Must be positive integer value.';
});

defineRule('validPositiveOrZeroInteger', (value) => {
    const isPositiveInteger = (str) => {
        return /^[0-9]\d*$/.test(str);
    };

    if (!value || isPositiveInteger(`${value}`)) {
        return true;
    }
    return 'Must be positive integer value or zero.';
});

defineRule('validPositive', (value) => {
    if (Number(value) < 0) {
        return 'Value cannot be less than zero.';
    }
    return true;
});

defineRule('validFloat', (value) => {
    if (!value || /^[-+]?\d{0,8}(?:\.\d{0,2}){0,1}$/.test(`${value}`)) {
        return true;
    }
    return 'Must be valid decimal value.';
});

defineRule('validPositiveFloat', (value) => {
    if (Number(value) === 0) {
        return 'Value cannot be zero.';
    }
    if (!value || /^[+]?\d{1,8}(?:\.\d{0,2}){0,1}$/.test(`${value}`)) {
        return true;
    }
    return 'Must be valid positive decimal value.';
});

defineRule('validPositiveOrZeroFloat', (value) => {
    if (!value || /^[+]?\d{1,8}(?:\.\d{0,2}){0,1}$/.test(`${value}`)) {
        return true;
    }
    return 'Must be valid decimal value or zero.';
});

defineRule('minValue', (value, [length]) => {
    if (+value > +length) {
        return true;
    }
    return `Value must be greater than ${length}.`;
});

defineRule('maxValue', (value, [length]) => {
    if (+value < +length) {
        return true;
    }
    return `Value must be less than ${length}.`;
});

defineRule('maxMessageLen', (value, [length, fieldName]) => {
    if (!value || value.length < +length) {
        return true;
    }
    return `${fieldName} must be less than ${length} characters.`;
});

defineRule('minMessageLen', (value, [length, fieldName]) => {
    if (value.length > +length) {
        return true;
    }
    return `${fieldName} must be greater than ${length} characters.`;
});

defineRule('logFormat', (value) => {
    const isValid = (str) =>
        Boolean(
            /^([0-9]|\d{2}|1\d{2}|2[0-4]\d|2[0-5][0-5])\.([0-9]|\d{2}|1\d{2}|2[0-4]\d|2[0-5][0-5])\.([0-9]|\d{2}|1\d{2}|2[0-4]\d|2[0-5][0-5])\.([0-9]|\d{2}|1\d{2}|2[0-4]\d|2[0-5][0-5])$/.test(
                str
            )
        );
    if (value && isValid(value)) {
        return true;
    }
    return 'Log format must be n.n.n.n (eg. 2.1.1.2).';
});

defineRule('noScript', (value) => {
    const noScriptRegex = new RegExp(/(\b)(on\S+)(\s*)=|javascript|(<\s*)(\/*)script/gi);
    if (!value || (value && Boolean(!noScriptRegex.test(value)))) {
        return true;
    }
    return 'Message contains restricted values.';
});

defineRule('invalidCaps', (value) => {
    const invalidCapsRex = /([A-Z][A-Z]+(?=\s[A-Z])(?:\s[A-Z][A-Z]+){3})/;
    if (!invalidCapsRex.test(value) && value !== value.toUpperCase()) {
        return true;
    }
    if (value === value.toUpperCase()) {
        return 'ALL CAPS are not allowed.';
    }
    return 'Cannot have more than 3 ALL CAPS words in a row.';
});

defineRule('validUrl', (value) => {
    const validUrlRex = /((([A-Za-z]{3,9}:(?:\/\/)?)(?:[-;:&=\+\$,\w]+@)?[A-Za-z0-9.-]+|(?:www.|[-;:&=\+\$,\w]+@)[A-Za-z0-9.-]+)((?:\/[\+~%\/.\w-_]*)?\??(?:[-\+=&;%@.\w_]*)#?(?:[\w]*))?)/;
    if (!value || validUrlRex.test(value)) {
        return true;
    }
    return 'URL must start from  http:// or https://';
});

defineRule('validHost', (value) => {
    const validHostRex = /^(([a-zA-Z0-9]|[a-zA-Z0-9][a-zA-Z0-9\-]*[a-zA-Z0-9])\.)*([A-Za-z0-9]|[A-Za-z0-9][A-Za-z0-9\-]*[A-Za-z0-9])$/;
    if (validHostRex.test(value)) {
        return true;
    }
    return 'Host must be valid.';
});

defineRule('validGoogleTrackingID', (value) => {
    const validGoogleTrackingIDRex = /^(G|UA|YT|MO)-[a-zA-Z0-9-]+$/;
    if (validGoogleTrackingIDRex.test(value)) {
        return true;
    }
    return 'Google Tracking Code must be valid.';
});

export const commaSeparatedEmailValidator = (value) => {
    if (!value) {
        return true;
    }
    const emails = value.split(',');
    const isValid = emails.every((item) => {
        return email(item.trim());
    });
    if (isValid) {
        return true;
    }
    return 'This field should be an email or comma-separated emails.';
};

defineRule('commaSeparatedEmail', commaSeparatedEmailValidator);

defineRule('uniqueOrderNumber', async (value) => {
    try {
        const response = await API.post(`/app/work-orders/api/orders/check-workorder/`, {
            work_order_number: value,
        });
        if (response.data && response.data.status === 'OK') {
            return true;
        }
    } catch {
        return 'Not unique order number.';
    }

    return 'Not unique order number.';
});

defineRule('validLatitude', (value) => {
    if (isFinite(value) && Math.abs(value) <= 90) {
        return true;
    }
    return 'Must be valid Latitude value.';
});

defineRule('validLongitude', (value) => {
    if (isFinite(value) && Math.abs(value) <= 180) {
        return true;
    }
    return 'Must be valid Longitude value.';
});

defineRule('maxMessageLenOrEqual', (value, params) => {
    const [length, fieldName] = params;

    if (!value || value.length <= +length) {
        return true;
    }
    return `${fieldName} must be less than ${length} characters.`;
});

defineRule('validRoutingNumber', (value) => {
    if (value.length !== 9) {
        return 'Routing number length must be equal to 9.';
    }
    const validRoutingNumberPattern = /^[0-9]{9}$/;
    if (validRoutingNumberPattern.test(value)) {
        return true;
    }
    return 'Routing number must contain only numbers.';
});

defineRule('validAccountNumber', (value) => {
    if (value.length < 5 || value.length > 17) {
        return 'Account number length must be less than 18 and greater than 4.';
    }
    const validAccountNumberPattern = /^[0-9]{5,17}$/;
    if (validAccountNumberPattern.test(value)) {
        return true;
    }
    return 'Account number must contain only numbers.';
});

defineRule('maxValueOrEqual', (value, [length]) => {
    if (+value <= +length) {
        return true;
    }
    return `Value must be less or equal ${length}.`;
});

defineRule('validNumber', (value) => {
    if (!isNaN(value)) {
        return true;
    }
    return 'Must be valid number.';
});

// extend('maxLength', {
//     validate(value, args) {
//         return value.length <= args.length;
//     },
//     params: ['length'],
//     message: 'This field must be no longer than {length} characters.',
// });

defineRule('maxLength', (value, args) => {
    if (value.length > args.length) {
        return true;
    }
    return 'This field must be no longer than {length} characters.';
});

defineRule('pixelValue', (value) => {
    const pixelRegex = /^\d+px$/; // Matches one or more digits followed by "px"
    if (!value || pixelRegex.test(value)) {
        return true;
    }

    return `Use a value ending with 'px' (e.g., '60px').`;
});
